import { Num, Plural, T, useGT } from 'gt-react';
import { useSelector } from '@tanstack/react-store';
import {
  arTaskAssigneeKey,
  type ArTaskRow,
  type ArTasksAssigneeGroup,
} from '@erp/rpc';
import type { ReactTable, Row } from '@/lib/tanstack-table';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/utils/formatters';
import {
  assigneeGroupStartingAt,
  groupSelectionState,
} from './arTasksAssigneeGroups';

/**
 * An assignee group's header line: a checkbox for the group's rows on this
 * page (for bulk Assign / Close), the user or agent (or Unassigned), and the
 * group's task count and amount over every page.
 */
function ArTasksAssigneeGroupHeader({
  group,
  table,
}: {
  group: ArTasksAssigneeGroup;
  table: ReactTable<ArTaskRow>;
}) {
  // The table's identity never changes, so the compiler would freeze the
  // checkbox in its first state (see DataTableSelectAllCheckbox).
  'use no memo';
  const gt = useGT();
  const selection = useSelector(table.store, (state) => state.rowSelection);
  const groupRows = table
    .getRowModel()
    .rows.filter((row) => arTaskAssigneeKey(row.original) === group.key);
  const name =
    group.name ??
    gt('Unassigned', {
      context: 'AR tasks group header, tasks with no assignee',
    });
  return (
    <div className="flex items-center gap-3 text-sm">
      <Checkbox
        checked={groupSelectionState(
          groupRows.map((row) => row.id),
          selection,
        )}
        onCheckedChange={(value) =>
          groupRows.forEach((row) => row.toggleSelected(value === true))
        }
        onClick={(event) => event.stopPropagation()}
        aria-label={gt("Select {name}'s tasks on this page", {
          context: 'AR tasks group header checkbox, accessible label',
          name,
        })}
      />
      <span className="font-medium">{name}</span>
      <span className="text-muted-foreground">
        <T context="AR tasks group header, the group's task count over every page">
          <Num>{group.count}</Num>{' '}
          <Plural n={group.count} one="task" other="tasks" />
        </T>
        {' · '}
        {formatCurrency(Number(group.amount), { currency: 'USD' })}
      </span>
    </div>
  );
}

/** DataTable's `groupHeaderFn` for the assignee groups; undefined when ungrouped. */
export function assigneeGroupHeaderFn(
  groups: ArTasksAssigneeGroup[] | undefined,
  table: ReactTable<ArTaskRow>,
): ((row: Row<ArTaskRow>, index: number) => React.ReactNode) | undefined {
  if (!groups) return undefined;
  return (_row, index) => {
    const rows = table.getRowModel().rows.map((row) => row.original);
    const group = assigneeGroupStartingAt(rows, groups, index);
    return group ? (
      <ArTasksAssigneeGroupHeader group={group} table={table} />
    ) : null;
  };
}
